// REC7-S2 (docs/build-briefs/REC7-S2-cross-franchise-recipe-share.md; DEC-426 PROVISIONAL; DEC-339, P0.5): the pure decisions behind
// the franchisee's "Share with other franchisees" area in Settings > Recipes. No DOM, no fetch:
// test/rec7-s2-recipe-share-ui-model.test.ts proves it.
//
// The draft is the franchisor console's recipe draft (a name, a category and descriptive ingredients), so the request has no
// field that could hold an inventory item. The recipients are the other franchisees of the same network, named by the opaque
// reference the server sent, A to Z; a share may also be submitted to corporate. Labels only; an empty list shows N/A.

import {
  emptyRecipeDraft,
  recipeDraftToBody,
  type RecipeDraft,
  type RecipeDraftField,
} from "./franchisorRecipeModel.ts";
import { formatRecipeQuantity } from "./catalogDisplay.ts";
import { sortByName } from "./nameSort.ts";

export {
  emptyRecipeDraft,
  emptySlot,
  parseSlotAmount,
  type RecipeDraft,
} from "./franchisorRecipeModel.ts";

export interface SharePeerDto {
  ref: string;
  name: string;
}

export type ShareRecipientStateDto = "pending" | "needs_mapping" | "adopted" | "declined";

export interface ShareRecipientDto {
  ref?: string;
  name: string;
  state: ShareRecipientStateDto;
  at: string | null;
}

export interface ShareSlotDto {
  position: number;
  name: string;
  category: string;
  amount: string;
  unit: string;
  notes: string | null;
}

export interface ShareProposalDto {
  status: "pending" | "accepted" | "declined" | "withdrawn";
  note: string | null;
  decidedAt: string | null;
}

export interface RecipeShareDto {
  /** FRRECPROP1: the status of the proposal to corporate; absent or null when not submitted. */
  proposal?: ShareProposalDto | null;
  publicationId: string;
  name: string;
  category: string;
  state: "published" | "withdrawn";
  toFranchisor: boolean;
  createdAt: string;
  withdrawnAt: string | null;
  slots: ShareSlotDto[];
  recipients: ShareRecipientDto[];
}

export interface RecipeSharesDto {
  inNetwork: boolean;
  peers: SharePeerDto[];
  shares: RecipeShareDto[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isRecipeSharesShape(value: unknown): value is RecipeSharesDto {
  if (!isObject(value)) return false;
  if (typeof value["inNetwork"] !== "boolean") return false;
  const peers = value["peers"];
  const shares = value["shares"];
  if (!Array.isArray(peers) || !Array.isArray(shares)) return false;
  const peersOk = peers.every(
    (p) => isObject(p) && typeof p["ref"] === "string" && typeof p["name"] === "string",
  );
  const sharesOk = shares.every(
    (s) =>
      isObject(s) &&
      typeof s["publicationId"] === "string" &&
      typeof s["name"] === "string" &&
      typeof s["category"] === "string" &&
      (s["state"] === "published" || s["state"] === "withdrawn") &&
      typeof s["toFranchisor"] === "boolean" &&
      (s["proposal"] === undefined ||
        s["proposal"] === null ||
        (isObject(s["proposal"]) &&
          (s["proposal"]["status"] === "pending" ||
            s["proposal"]["status"] === "accepted" ||
            s["proposal"]["status"] === "declined" ||
            s["proposal"]["status"] === "withdrawn") &&
          (s["proposal"]["note"] === null || typeof s["proposal"]["note"] === "string"))) &&
      Array.isArray(s["slots"]) &&
      (s["slots"] as unknown[]).every(
        (slot) =>
          isObject(slot) &&
          typeof slot["position"] === "number" &&
          typeof slot["name"] === "string" &&
          typeof slot["amount"] === "string" &&
          typeof slot["unit"] === "string",
      ) &&
      Array.isArray(s["recipients"]) &&
      (s["recipients"] as unknown[]).every(
        (rc) =>
          isObject(rc) &&
          typeof rc["name"] === "string" &&
          (rc["state"] === "pending" ||
            rc["state"] === "needs_mapping" ||
            rc["state"] === "adopted" ||
            rc["state"] === "declined"),
      ),
  );
  return peersOk && sharesOk;
}

/** The franchisees to choose from, A to Z (DEC-339), whatever order the server sent; the input is not mutated. */
export function sharePeerChoices(peers: readonly SharePeerDto[]): SharePeerDto[] {
  return sortByName(peers, (p) => p.name);
}

export const SHARE_TARGET_MISSING = "Choose a franchisee to share with, or submit it to corporate.";

export type ShareDraftResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; field: RecipeDraftField | "recipients"; message: string; slotIndex?: number };

/** The request body for a share, or the first problem. The server checks everything again. */
export function shareDraftToBody(
  draft: RecipeDraft,
  selectedRefs: ReadonlySet<string>,
  toFranchisor: boolean,
): ShareDraftResult {
  // The draft's own validation wants a location; a share has none, so a placeholder is passed and its two keys are dropped.
  const made = recipeDraftToBody(draft, "optional", new Set(["share"]));
  if (!made.ok) return made;
  if (selectedRefs.size === 0 && !toFranchisor) {
    return { ok: false, field: "recipients", message: SHARE_TARGET_MISSING };
  }
  const { adoption: _adoption, locationIds: _locationIds, ...body } = made.body;
  void _adoption;
  void _locationIds;
  return { ok: true, body: { ...body, recipientRefs: [...selectedRefs], toFranchisor } };
}

/** The form's error line after a recipient is ticked or cleared: the "choose" message goes once a target exists. */
export function errorAfterTargetChange(message: string, ticked: number, toFranchisor: boolean): string {
  return message === SHARE_TARGET_MISSING && (ticked > 0 || toFranchisor) ? "" : message;
}

export function recipientStateLabel(state: ShareRecipientStateDto): string {
  switch (state) {
    case "pending":
      return "Not answered";
    case "needs_mapping":
      return "Needs mapping";
    case "adopted":
      return "Accepted";
    case "declined":
      return "Declined";
  }
}

export interface RecipeShareView {
  publicationId: string;
  name: string;
  /** "Classics. Whiskey 2.00 oz, Simple syrup 0.25 oz". */
  detailText: string;
  /** "Dana Location: Accepted, Lee Location: Not answered", with "Corporate" first when it was submitted; N/A when empty. */
  recipientsText: string;
  stateLabel: string;
  tone: "good" | "neutral";
  canWithdraw: boolean;
}

export function shareViews(shares: readonly RecipeShareDto[]): RecipeShareView[] {
  return sortByName(shares, (s) => s.name).map((s) => {
    const slots = [...s.slots]
      .sort((a, b) => a.position - b.position)
      .map((slot) => `${slot.name} ${formatRecipeQuantity(slot.amount)} ${slot.unit}`)
      .join(", ");
    const named = sortByName(s.recipients, (rc) => rc.name).map(
      (rc) => `${rc.name}: ${recipientStateLabel(rc.state)}`,
    );
    const parts = [...(s.toFranchisor ? ["Corporate"] : []), ...named];
    return {
      publicationId: s.publicationId,
      name: s.name,
      detailText: `${s.category}. ${slots}`,
      recipientsText: parts.length === 0 ? "-" : parts.join(", "),
      stateLabel: s.state === "published" ? "Shared" : "Withdrawn",
      tone: s.state === "published" ? "good" : "neutral",
      canWithdraw: s.state === "published",
    };
  });
}

export const SHARE_FAILED_MESSAGE = "The recipe could not be shared. Nothing was changed. Try again.";
export const SHARE_LOAD_FAILED =
  "Your network's franchisees could not be loaded. Check your connection and reopen this screen.";

/** Words for a failed share or withdraw. The server's sentence for a known refusal is plain language. */
export function describeShareFailure(status: number, body: unknown): string {
  const record = isObject(body) ? body : {};
  const code = record["error"];
  const message = record["message"];
  if (status === 0) return "Could not reach the server. Check your connection and try again.";
  if (status === 403) return "You do not have permission to share recipes.";
  if (status === 404) return "One of those franchisees is no longer in your network. Reopen this screen and choose again.";
  if (
    (code === "publication_name_taken" || code === "invalid_request" || code === "not_in_network") &&
    typeof message === "string" &&
    message !== ""
  ) {
    return message;
  }
  return SHARE_FAILED_MESSAGE;
}

export function sharedMessage(name: string): string {
  return `${name} shared.`;
}
export function withdrawnMessage(name: string): string {
  return `${name} withdrawn.`;
}

export { emptyRecipeDraft as emptyShareDraft };
